import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeartPulse } from '@fortawesome/pro-duotone-svg-icons';
import { Box, Paper, Typography } from '@mui/material';
import {
  dimGrey,
  inactive,
  lightBlack,
  lightPurple,
} from '../../../../../mainTheme';

interface VitalsCardProps {
  data: {
    HR: string;
    SpO2: string;
    BT: string;
    WT: string;
    BP: string;
  };
  isSelected?: boolean;
  onClick?: () => void;
}

const valueStyle = {
  color: '#C0C0C8',
  textAlign: 'center',
  fontSize: 16,
  fontWeight: 700,
  lineHeight: '22px',
  letterSpacing: '0.80px',
};
const labelStyle = {
  color: dimGrey,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  textAlign: 'center',
  marginTop: '6px',
};

const VitalsCard: React.FC<VitalsCardProps> = ({
  data,
  isSelected,
  onClick,
}) => {
  return (
    <Paper
      elevation={isSelected ? 8 : 1}
      sx={{
        px: '24px',
        py: '21px',
        cursor: 'pointer',
        transition: 'all 0.3s',
        transform: isSelected ? 'scale(1.02)' : 'none',
        '&:hover': {
          elevation: 3,
        },
        borderRadius: '8px',
        border: isSelected
          ? `1px solid ${lightPurple}`
          : '1px solid var(--Outline-O5---Separator, #D4D4D9)',
        boxShadow: isSelected
          ? '-4px 8px 24px 0px rgba(0, 0, 0, 0.25)'
          : 'none',
        height: '152px',
        flex: '1 0 0',
        overflow: 'hidden',
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          height: '100%',
          width: '100%',
        }}
      >
        {/* Left: Icon/Title */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            minWidth: 100,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography
              sx={{
                color: isSelected ? lightBlack : dimGrey,
                fontSize: '16px',
                fontWeight: 700,
                letterSpacing: '0.2px',
                mr: 1,
              }}
            >
              Vitals
            </Typography>
            <FontAwesomeIcon
              icon={faHeartPulse}
              color={isSelected ? lightPurple : dimGrey}
            />
          </Box>
        </Box>
        {/* Right: Vitals */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            height: '100%',
          }}
        >
          {/* First row: HR & SpO2 */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={valueStyle}>{data.HR || 'N/A'}</Box>
              <Box sx={labelStyle}>HR</Box>
            </Box>
            <Box
              sx={{
                width: '1px',
                height: '32px',
                background: inactive,
                mx: 1,
                display: { xs: 'none', sm: 'block' },
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Box sx={valueStyle}>{data.SpO2 || 'N/A'}</Box>
              <Box sx={labelStyle}>SpO2</Box>
            </Box>
          </Box>
          {/* Second row: BT, WT, BP */}
          <Box
            sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={valueStyle}>{data.BT || 'N/A'}</Box>
              <Box sx={labelStyle}>BT</Box>
            </Box>
            <Box
              sx={{
                width: '1px',
                height: '32px',
                background: inactive,
                mx: 1,
                display: { xs: 'none', sm: 'block' },
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Box sx={valueStyle}>{data.WT || 'N/A'}</Box>
              <Box sx={labelStyle}>WT</Box>
            </Box>
            <Box
              sx={{
                width: '1px',
                height: '32px',
                background: inactive,
                mx: 1,
                display: { xs: 'none', sm: 'block' },
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Box sx={valueStyle}>{data.BP || 'N/A'}</Box>
              <Box sx={labelStyle}>BP</Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default VitalsCard;
